import {
  ApiResponse,
  EventScoreResponseModel,
} from '../models';
import http from '../utils/http';

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey =
  'admin-backend-auth-tokens';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey,
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function getBackend(
  backendName: string,
): BackendAuthToken {
  const backends =
    getBackendAuthTokens();

  const backend = backends.find(
    (item) =>
      item.name === backendName,
  );

  if (!backend) {
    throw new Error(
      `Backend "${backendName}" nije pronađen ili korisnik nije autentifikovan na tom backendu.`,
    );
  }

  return backend;
}

function getApiUrl(
  path: string,
  backendName: string,
): string {
  const backend =
    getBackend(backendName);

  return `${backend.url.replace(
    /\/+$/,
    '',
  )}${path}`;
}

/**
 * Get result history for event.
 */
export async function getResultHistoryByEventId(
  eventId: number,
  backendName: string,
): Promise<EventScoreResponseModel[]> {
  const response: ApiResponse<
    EventScoreResponseModel[]
  > = await http.get(
    getApiUrl(
      `/api/events/event-result-history/${eventId}`,
      backendName,
    ),
    {
      backend: backendName,
    } as any,
  );

  return response.data;
}

/**
 * Get result history + logs for event.
 */
export async function getResultHistoryAndLogsByEventId(
  eventId: number,
  backendName: string,
): Promise<EventScoreResponseModel[]> {
  const response: ApiResponse<
    EventScoreResponseModel[]
  > = await http.get(
    getApiUrl(
      `/api/events/event-result-history-log/${eventId}`,
      backendName,
    ),
    {
      backend: backendName,
    } as any,
  );

  return response.data;
}

/**
 * Update result history.
 */
export async function updateResultHistoryByEventId(
  resultId: number,
  time: string,
  parsedScore: string,
  isOvertime: any,
  backendName: string,
): Promise<any> {
  const reqData: any = {
    time,
    parsedScore,
    isOvertime,
  };

  const response: ApiResponse<any> =
    await http.patch(
      getApiUrl(
        `/api/events/result-history/${resultId}`,
        backendName,
      ),
      reqData,
      {
        headers: {
          successAlert: true,
        },
        withCredentials: true,
        backend: backendName,
      } as any,
    );

  return response.data;
}

/**
 * Add new result history.
 */
export async function addNewResultHistoryByEventId(
  eventResult: EventScoreResponseModel,
  backendName: string,
): Promise<EventScoreResponseModel> {
  const requestData = {
    time: eventResult.time,
    parsedScore:
      eventResult.parsedScore,
    rowIdx: eventResult.rowIdx,
  };

  const response: ApiResponse<
    EventScoreResponseModel
  > = await http.post(
    getApiUrl(
      `/api/events/result-history/${eventResult.idEvent}`,
      backendName,
    ),
    requestData,
    {
      headers: {
        successAlert: true,
      },
      withCredentials: true,
      backend: backendName,
    } as any,
  );

  return response.data;
}


/**
 * Get feed.
 */
export async function getFeedService(
  eventHash: string,
  backendName: string,
) {
  const response: ApiResponse<any> =
    await http.get(
      getApiUrl(
        '/api/events/score-parser/get-feed',
        backendName,
      ),
      {
        params: {
          eventHash,
        },
        backend: backendName,
      } as any,
    );

  return response.data;
}

/**
 * Delete / clear result history.
 */
export async function clearResultHistoryById(
  eventId: number,
  backendName: string,
): Promise<EventScoreResponseModel[]> {
  const response: ApiResponse<
    EventScoreResponseModel[]
  > = await http.delete(
    getApiUrl(
      `/api/events/result-history/${eventId}`,
      backendName,
    ),
    {
      withCredentials: true,
      backend: backendName,
    } as any,
  );

  return response.data;
}